import { useState, useEffect } from "react";
import { Switch, CircularProgress } from "@mui/material";
import {
  IcoX,
  IcoUser,
  IcoBuilding2,
  IcoChevLeft,
  IcoChevRight,
  IcoDownload,
} from "./icons";

const PANEL_PAGE_SIZE = 5;
const PANEL_PAG_MAX_BTNS = 5;

const escapeCsvValue = (value) => {
  const normalizedValue = value == null ? "" : String(value);
  if (
    normalizedValue.includes(",") ||
    normalizedValue.includes('"') ||
    normalizedValue.includes("\n")
  ) {
    return `"${normalizedValue.replace(/"/g, '""')}"`;
  }
  return normalizedValue;
};

const toTitleCaseHeader = (value) =>
  String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const triggerCsvDownload = (headers, rows, fileName) => {
  const csvContent = [headers, ...rows]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
  const blob = new Blob(["\uFEFF", csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

// Mirrors buildSingleRecordVendorDataset in DetailSidePanel.jsx — dynamic
// headers built from whatever fields the vendor candidates actually carry.
const buildVendorExportDataset = (vendorList) => {
  const getExportRowObject = (candidate) => ({
    ...(candidate?.raw && typeof candidate.raw === "object"
      ? candidate.raw
      : {}),
    enterpriseId: candidate.enterpriseId,
    name: candidate.name,
    primarySpeciality: candidate.primarySpeciality,
    address: candidate.address,
    country: candidate.city,
    licenseId: candidate.licenseId,
    classificationCode: candidate.classificationCode,
  });

  const excludedHeaders = new Set(["raw", "source type"]);
  const fieldKeys = [];
  const fieldKeySet = new Set();
  vendorList.forEach((candidate) => {
    Object.keys(getExportRowObject(candidate)).forEach((key) => {
      if (key === "raw" || fieldKeySet.has(key)) return;
      fieldKeySet.add(key);
      fieldKeys.push(key);
    });
  });

  const uniqueColumns = [];
  const seenHeaders = new Set();
  fieldKeys.forEach((key) => {
    const header = toTitleCaseHeader(key);
    const normalizedHeader = header.toLowerCase();
    if (excludedHeaders.has(normalizedHeader) || seenHeaders.has(normalizedHeader)) {
      return;
    }
    seenHeaders.add(normalizedHeader);
    uniqueColumns.push({ key, header });
  });

  const rows = vendorList.map((candidate, index) => {
    const rowObject = getExportRowObject(candidate);
    return [
      index + 1,
      ...uniqueColumns.map((column) => {
        const value = rowObject[column.key];
        if (value === undefined || value === null) return "";
        if (typeof value === "object") {
          try {
            return JSON.stringify(value);
          } catch (error) {
            return String(value);
          }
        }
        return value;
      }),
    ];
  });

  return { headers: ["Row", ...uniqueColumns.map((c) => c.header)], rows };
};

// Builds a condensed page list like [1, "...", 4, 5, 6, "...", 12] so the
// pagination bar stays compact instead of rendering a button per page.
function getPanelPageNumbers(current, total, maxBtns = PANEL_PAG_MAX_BTNS) {
  if (total <= maxBtns) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current]);
  const siblings = Math.floor((maxBtns - 3) / 2);
  for (let i = 1; i <= siblings; i++) {
    if (current - i > 1) pages.add(current - i);
    if (current + i < total) pages.add(current + i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) result.push("...");
    result.push(p);
    prev = p;
  }
  return result;
}

// ─── Candidate Card ───────────────────────────────────────────────────────────

function CandidateCard({ entity, selected, onSelect, readonly, type }) {
  const isHCP = type === "hcp";
  const hcp = isHCP ? entity : null;
  const selCls = selected ? (isHCP ? "sel-hcp" : "sel-hco") : "";
  return (
    <label
      className={`rc-cand${selCls ? " " + selCls : ""}${readonly ? " ro" : ""}`}
    >
      <div className="rc-cand-radio">
        <input
          type="radio"
          checked={selected}
          onChange={onSelect}
          disabled={readonly}
          style={{ accentColor: isHCP ? "#0284c7" : "#059669" }}
        />
      </div>
      <div className="rc-cand-info">
        <span className={`rc-cand-id ${type}`}>{entity.enterpriseId}</span>
        <span className="rc-cand-name">{entity.name}</span>
        {hcp && <span className="rc-cand-det">{hcp.primarySpeciality}</span>}
        <span className="rc-cand-det">
          {entity.address}, {entity.city}
        </span>
        <span className="rc-cand-mono">{entity.licenseId}</span>
        <span className="rc-cand-det">{entity.classificationCode}</span>
      </div>
    </label>
  );
}

// ─── Side Panel ───────────────────────────────────────────────────────────────

export default function SidePanel({
  record,
  candidates,
  isAutoProcessed,
  selection,
  onSelectionChange,
  onClose,
  leftEntityType,
  rightEntityType,
  vendorSearchState,
  onVendorSearch,
}) {
  const [panelTab, setPanelTab] = useState("hcp");
  const [hcpPage, setHcpPage] = useState(1);
  const [hcoPage, setHcoPage] = useState(1);
  const [activeSourceByTab, setActiveSourceByTab] = useState({});

  // Only show the HCP / HCO(Parent HCO) tab the row's upload input actually
  // carries — e.g. an HCO-only input shouldn't also render an empty HCP tab.
  const visibleTabs = (() => {
    const tabs = ["hcp", "hco"].filter((t) =>
      t === "hcp" ? record.hasHcp !== false : record.hasHco !== false,
    );
    return tabs.length > 0 ? tabs : ["hcp", "hco"];
  })();

  useEffect(() => {
    setPanelTab(visibleTabs[0]);
    setHcpPage(1);
    setHcoPage(1);
    setActiveSourceByTab({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.id]);

  const activeEntityType =
    panelTab === "hcp" ? leftEntityType || "hcp" : rightEntityType || "hco";
  const activeEntityLabel =
    panelTab === "hcp"
      ? activeEntityType === "hco"
        ? "HCO"
        : "HCP"
      : activeEntityType === "parent_hco"
        ? "Parent HCO"
        : "HCO";
  const activeVendorStatus = vendorSearchState?.[activeEntityType];
  const vendorSearchInProgress =
    activeVendorStatus === "SUBMITTED_IN_QUEUE" ||
    activeVendorStatus === "IN_PROGRESS";

  const activeList = panelTab === "hcp" ? candidates.hcps : candidates.hcos;
  // Real records carry a per-source split so we can offer the EPH/Vendor toggle.
  const sourceBuckets = candidates.sourceBuckets;
  const ephList = sourceBuckets
    ? panelTab === "hcp"
      ? sourceBuckets.eph.hcps
      : sourceBuckets.eph.hcos
    : null;
  const vendorList = sourceBuckets
    ? panelTab === "hcp"
      ? sourceBuckets.vendor.hcps
      : sourceBuckets.vendor.hcos
    : null;
  const defaultSourceTab = ephList && ephList.length > 0 ? "eph" : "vendor";
  const activeSourceTab = activeSourceByTab[panelTab] || defaultSourceTab;
  const isVendorTabInProgress =
    activeSourceTab === "vendor" && vendorSearchInProgress;
  // Once vendor search has been opted for an entity, keep showing the toggle
  // (not the button) even after refresh — either the in-memory click status
  // or already-fetched vendor candidates confirm the opt-in happened.
  const hasVendorDataForActiveEntity = !!vendorList && vendorList.length > 0;
  const shouldShowVendorToggle =
    !isAutoProcessed &&
    !!sourceBuckets &&
    (activeVendorStatus !== undefined || hasVendorDataForActiveEntity);
  const filteredList = sourceBuckets
    ? activeSourceTab === "eph"
      ? ephList
      : vendorList
    : activeList;

  const panelPage = panelTab === "hcp" ? hcpPage : hcoPage;
  const setPanelPage = panelTab === "hcp" ? setHcpPage : setHcoPage;
  const totalPanelPages = Math.max(
    1,
    Math.ceil(filteredList.length / PANEL_PAGE_SIZE),
  );
  const pagedList = isAutoProcessed
    ? filteredList
    : filteredList.slice(
        (panelPage - 1) * PANEL_PAGE_SIZE,
        panelPage * PANEL_PAGE_SIZE,
      );

  const switchTab = (t) => {
    setPanelTab(t);
  };

  return (
    <>
      <div className="rc-sp">
        <div className="rc-sp-hdr">
          <div>
            <p className="rc-sp-ttl">Record Details</p>
            {/* <p className="rc-sp-id">{record.id}</p> */}
          </div>
          <button className="rc-sp-close" onClick={onClose}>
            <IcoX size={15} />
          </button>
        </div>

        <div className="rc-sp-tabs">
          {visibleTabs.map((t) => {
            const key = t === "hcp" ? "hcps" : "hcos";
            // Badge reflects the currently toggled EPH/Vendor source for
            // that tab (not the combined total) once source buckets exist.
            const tabBadgeCount = sourceBuckets
              ? sourceBuckets[
                  activeSourceByTab[t] ||
                    (sourceBuckets.eph?.[key]?.length > 0 ? "eph" : "vendor")
                ]?.[key]?.length || 0
              : t === "hcp"
                ? candidates.hcps.length
                : candidates.hcos.length;
            const isTabSelected =
              !isAutoProcessed &&
              (t === "hcp" ? selection.hcpIdx >= 0 : selection.hcoIdx >= 0);
            return (
              <button
                key={t}
                className={`rc-sp-tab${panelTab === t ? " on" : ""}`}
                onClick={() => switchTab(t)}
              >
                {t === "hcp" && leftEntityType !== "hco" ? (
                  <IcoUser size={13} />
                ) : (
                  <IcoBuilding2 size={13} />
                )}
                {t === "hcp"
                  ? leftEntityType === "hco"
                    ? "HCO"
                    : "HCP"
                  : rightEntityType === "parent_hco"
                    ? "Parent HCO"
                    : "HCO"}
                <span className="rc-sp-tab-bdg">{tabBadgeCount}</span>
                {isTabSelected && (
                  <span className="rc-sp-tab-check">✓</span>
                )}
              </button>
            );
          })}
        </div>

        {!isAutoProcessed && onVendorSearch && (
          <div className="rc-vs-bar">
            {shouldShowVendorToggle ? (
              <div
                className="rc-vs-closed"
                style={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    border: "1px solid #d1d5db",
                    background: "#f9fafb",
                    borderRadius: 6,
                    padding: "2px 8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: activeSourceTab === "eph" ? "#1d4ed8" : "#6b7280",
                    }}
                  >
                    EPH
                  </span>
                  <Switch
                    size="small"
                    checked={activeSourceTab === "vendor"}
                    onChange={(event) =>
                      setActiveSourceByTab((prev) => ({
                        ...prev,
                        [panelTab]: event.target.checked ? "vendor" : "eph",
                      }))
                    }
                    inputProps={{ "aria-label": "eph vendor switch" }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color:
                        activeSourceTab === "vendor" ? "#059669" : "#6b7280",
                    }}
                  >
                    Vendor
                  </span>
                </div>
                {activeSourceTab === "vendor" && hasVendorDataForActiveEntity && (
                  <button
                    type="button"
                    className="rc-vs-export-btn"
                    onClick={() => {
                      const dataset = buildVendorExportDataset(vendorList);
                      if (dataset.rows.length === 0) return;
                      triggerCsvDownload(
                        dataset.headers,
                        dataset.rows,
                        `vendor_${activeEntityType}_record_${record.id}.csv`,
                      );
                    }}
                  >
                    <IcoDownload size={11} />
                    {`Export ${activeEntityLabel} Vendor Result`}
                  </button>
                )}
              </div>
            ) : (
              <div className="rc-vs-closed">
                <span className="rc-vs-count">No vendor search run yet</span>
                <button
                  className="rc-vs-open-btn"
                  onClick={() => onVendorSearch(activeEntityType)}
                >
                  <IcoBuilding2 size={11} />
                  {`${activeEntityLabel} Vendor Search`}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="rc-sp-body">
          {activeSourceTab === "vendor" && vendorSearchInProgress && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 8,
                marginTop: 20,
                marginBottom: 16,
                fontSize: 13,
                color: "#6b7280",
              }}
            >
              <CircularProgress size={16} sx={{ color: "rgb(204, 31, 31)" }} />
              {`${activeEntityLabel} status: ${
                activeVendorStatus === "IN_PROGRESS"
                  ? "In Progress"
                  : "Submitted in Queue"
              }`}
            </div>
          )}
          {!isAutoProcessed &&
            panelTab === "hcp" &&
            selection.hcpIdx >= 0 &&
            candidates.hcps[selection.hcpIdx] && (
              <div className="rc-sp-selected-bar hcp">
                <span className="rc-sp-selected-text">
                  ✓ Selected Result:{" "}
                  {candidates.hcps[selection.hcpIdx].enterpriseId}
                </span>
                <button
                  type="button"
                  className="rc-sp-clear-btn hcp"
                  onClick={() =>
                    onSelectionChange({ ...selection, hcpIdx: -1 })
                  }
                >
                  Clear {leftEntityType === "hco" ? "HCO" : "HCP"} Selection
                </button>
              </div>
            )}
          {!isAutoProcessed &&
            panelTab === "hco" &&
            selection.hcoIdx >= 0 &&
            candidates.hcos[selection.hcoIdx] && (
              <div className="rc-sp-selected-bar hco">
                <span className="rc-sp-selected-text">
                  ✓ Selected Result:{" "}
                  {candidates.hcos[selection.hcoIdx].enterpriseId}
                </span>
                <button
                  type="button"
                  className="rc-sp-clear-btn hco"
                  onClick={() =>
                    onSelectionChange({ ...selection, hcoIdx: -1 })
                  }
                >
                  Clear {activeEntityLabel} Selection
                </button>
              </div>
            )}
          {/* While a vendor search is running, only the status line above is shown — suppress the empty-state message */}
          {panelTab === "hcp" &&
            !isVendorTabInProgress &&
            (filteredList.length === 0 ? (
              <p className="rc-sp-empty">
                {isAutoProcessed
                  ? "No Result Found"
                  : activeSourceTab === "vendor"
                    ? "No results found"
                    : `No ${leftEntityType === "hco" ? "HCO" : "HCP"} candidates`}
              </p>
            ) : (
              pagedList.map((h) => {
                const i = candidates.hcps.indexOf(h);
                return (
                  <CandidateCard
                    key={h.enterpriseId}
                    entity={h}
                    type={leftEntityType === "hco" ? "hco" : "hcp"}
                    selected={selection.hcpIdx === i}
                    onSelect={() =>
                      !isAutoProcessed &&
                      onSelectionChange({ ...selection, hcpIdx: i })
                    }
                    readonly={isAutoProcessed}
                  />
                );
              })
            ))}
          {panelTab === "hco" &&
            !isVendorTabInProgress &&
            (filteredList.length === 0 ? (
              <p className="rc-sp-empty">
                {isAutoProcessed
                  ? "No Result Found"
                  : activeSourceTab === "vendor"
                    ? "No results found"
                    : `No ${activeEntityLabel} candidates`}
              </p>
            ) : (
              pagedList.map((h) => {
                const i = candidates.hcos.indexOf(h);
                return (
                  <CandidateCard
                    key={h.enterpriseId}
                    entity={h}
                    type="hco"
                    selected={selection.hcoIdx === i}
                    onSelect={() =>
                      !isAutoProcessed &&
                      onSelectionChange({ ...selection, hcoIdx: i })
                    }
                    readonly={isAutoProcessed}
                  />
                );
              })
            ))}
        </div>

        {!isAutoProcessed && filteredList.length > 0 && totalPanelPages > 1 && (
          <div className="rc-sp-pag">
            <span className="rc-sp-pag-info">
              {(panelPage - 1) * PANEL_PAGE_SIZE + 1}–
              {Math.min(panelPage * PANEL_PAGE_SIZE, filteredList.length)} of{" "}
              {filteredList.length}
            </span>
            <div className="rc-sp-pag-btns">
              <button
                className="rc-sp-pag-btn"
                disabled={panelPage === 1}
                onClick={() => setPanelPage((p) => Math.max(1, p - 1))}
              >
                <IcoChevLeft size={12} />
              </button>
              {getPanelPageNumbers(panelPage, totalPanelPages).map((p, i) =>
                p === "..." ? (
                  <span key={`ellipsis-${i}`} className="rc-sp-pag-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`rc-sp-pag-btn${p === panelPage ? " on" : ""}`}
                    onClick={() => setPanelPage(p)}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                className="rc-sp-pag-btn"
                disabled={panelPage === totalPanelPages}
                onClick={() =>
                  setPanelPage((p) => Math.min(totalPanelPages, p + 1))
                }
              >
                <IcoChevRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
